import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, ViewChildren, QueryList, signal, computed, inject, input, output } from "@angular/core";
import {
  AgentParam,
  AppConfigService,
  ApplicationService,
  IInteraction,
  IQueue,
} from "../../../../core-ui";
import { QueueService } from "../../../services";
import { IShareLinkAction } from "../share-link.component";
import { GenesysInvitationChannelEnum } from "../../../models";
import {
  IMenuItemContainer,
  MENU_ITEM_CONTAINER_TOKEN,
  MenuComponent,
  MenuItemComponent,
} from "../../../../modules/shared/components";

@Component({
  selector: "app-share-link-sms",
  templateUrl: "./sms.component.html",
  styleUrls: ["./sms.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MENU_ITEM_CONTAINER_TOKEN,
      useExisting: ShareLinkSmsComponent,
    },
  ],
  standalone: false,
})
export class ShareLinkSmsComponent
  implements OnInit, IShareLinkAction, IMenuItemContainer
{
  private queueService = inject(QueueService);
  private cdr = inject(ChangeDetectorRef);
  private config = inject(AppConfigService);
  private application = inject(ApplicationService);
  private menu = inject(MenuComponent, { optional: true });

  readonly interaction = input<IInteraction>(undefined);
  readonly link = input<string>(undefined);

  readonly completed = output<void>();
  readonly openChange = output<boolean>();
  readonly confirm = output<{
    channel: GenesysInvitationChannelEnum;
    queue?: IQueue;
    workflow?: string;
}>();

  @ViewChildren(MenuItemComponent)
  menuItems: QueryList<MenuItemComponent>;

  smsQueues = signal<IQueue[]>([]);
  isSelectingSMSQueue = signal(false);
  isLoadingQueues = signal(false);
  SMSQueue: IQueue;
  phoneNumber: string;

  async ngOnInit() {
    if (!this.isWorkflowEnabled) {
      this.isLoadingQueues.set(true);
      try {
        this.smsQueues.set(await this.queueService.getSMSQueues());
      } finally {
        this.isLoadingQueues.set(false);
        this.notifyMenuContainer();
      }
    }
  }

  getMenuItems(): MenuItemComponent[] {
    return this.menuItems.toArray().filter((i) => i.type !== "header");
  }

  async shareLink() {
    this.isWorkflowEnabled ? this.shareUsingWorkflow() : this.shareUsingQueue();
  }

  expand() {
    if (!this.isWorkflowEnabled) {
      this.shareUsingQueue();
      this.notifyMenuContainer();
    }
  }

  collapse() {
    if (!this.isWorkflowEnabled) {
      this.isSelectingSMSQueue.set(false);
      this.notifyMenuContainer();
    }
  }

  private notifyMenuContainer() {
    this.cdr.detectChanges();
    this.menu?.notifyQueryListChanges();
  }

  async shareUsingWorkflow() {
    this.openChange.emit(false);
    this.confirm.emit({
      channel: GenesysInvitationChannelEnum.workflow,
      workflow: this.config.agentParam(AgentParam.SMS_WORKFLOW_ID),
    });
  }

  shareUsingQueue() {
    if (this.isSelectingSMSQueue()) {
      return this.isSelectingSMSQueue.set(false);
    }

    if (this.smsQueues().length > 1) {
      this.isSelectingSMSQueue.set(true);
    } else {
      this.SMSQueue = this.smsQueues()[0];
      this.openChange.emit(false);

      this.confirm.emit({
        channel: GenesysInvitationChannelEnum.queue,
        queue: this.SMSQueue,
      });
    }
  }

  smsQueueSelected(queue: IQueue) {
    this.SMSQueue = queue;
    this.openChange.emit(false);
    this.isSelectingSMSQueue.set(false);

    this.confirm.emit({
      channel: GenesysInvitationChannelEnum.queue,
      queue: this.SMSQueue,
    });
  }

  get isWorkflowEnabled() {
    return (
      this.config.agentParamEnabled(AgentParam.SMS_WORKFLOW_ENABLED) &&
      !!this.config.agentParam(AgentParam.SMS_WORKFLOW_ID)
    );
  }

  get isSMSAvailable() {
    return computed(() =>
      this.isWorkflowEnabled
        ? true
        : (this.application.isGenesysCloudApp || // always show sms in Genesys cloud
            this.interaction().supportsSMS()) &&
          (this.isLoadingQueues() || this.smsQueues().length > 0)
    );
  }
}
